import React, { createContext, useState, useContext, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth'; // Import auth functions
import { getAppConfig } from '../services/configService';

// Create the context
const AppConfigContext = createContext(null);

// Create a provider component
export const AppConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true); // Start loading initially
  const [error, setError] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false); // Track if auth state is determined

  useEffect(() => {
    const auth = getAuth();
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsAuthReady(true); // Mark auth state as determined
      if (user) {
        // User is signed in, fetch the config
        try {
          setLoading(true); // Set loading before fetch
          const appConfig = await getAppConfig();
          setConfig(appConfig);
          setError(null);
        } catch (err) {
          console.error("Failed to load app configuration:", err);
          setError(err.message || 'Failed to load configuration');
          setConfig(null); // Ensure config is null on error
        } finally {
          setLoading(false);
        }
      } else {
        // User is signed out
        setConfig(null); // Clear config if user signs out
        setError(null); // Clear any previous errors
        setLoading(false); // No longer loading config as user is out
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []); // Run only once on mount to set up listener

  const value = {
    config,
    // Combine auth readiness and config loading state
    loading: !isAuthReady || loading,
    error,
    getStatusByCode: (code) => config?.statuses?.find(s => s.code === code),
    getStatuses: () => config?.statuses || [],
  };

  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
};

// Create a custom hook to use the context
export const useAppConfig = () => {
  const context = useContext(AppConfigContext);
  if (context === undefined) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
};
